// ----------------------------------------------------------------------------
// Worship teams. Each team has its own roster, services, setlists and
// availability; the header's team switcher picks which one those pages show.
// Cards carry the team's setlist-mode default; below them, which positions
// people have served in on the active team.
// ----------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { useEntitlements } from '@admin/shared/contexts/EntitlementsContext';
import { groupsApi, plansApi } from '@app/api/organization';
import type { Group, GroupCreateRequest, ServicePlan } from '@app/types';
import { getTierLimits } from '@app/utils/tierUtils';
import { Toast } from '@app/components/Notifications';
import {
  AvatarStack,
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Menu,
  MenuDivider,
  MenuItem,
  Modal,
  ModalActions,
  PageHeader,
  Select,
  StatusPill,
  cx,
} from '@app/components/ui';
import { isPastDate } from '@app/lib/format';
import { logger } from '@app/utils/logger';
import { useChurchPeople } from '../../hooks/useChurchPeople';
import { useTeamHeadcounts } from '@app/hooks/useTeamHeadcounts';
import { ChurchMembershipState } from '@app/types';
import { SETLIST_MODE_LABEL, type SetlistMode } from '../../utils/setlistVisibility';

interface TeamForm {
  id?: string;
  name: string;
  leader: string;
  desc: string;
  /** Size limit as typed; '' means none. */
  cap: string;
  /** The team's saved limit, if any. */
  currentCap?: number;
  err?: string;
  capErr?: string;
}

/**
 * The organizations service stores an optional MaxCapacity per team and
 * refuses new members (invite code or admin add) once the team has that many.
 * Leaving maxCapacity out of an update keeps the current limit, so emptying
 * the field on a team that has one sends clearMaxCapacity instead.
 */
export function parseCapacity(raw: string, current?: number): { value?: number; clear?: boolean; error?: string } {
  const t = raw.trim();
  if (!t) return current ? { clear: true } : {};
  const n = Number(t);
  if (!Number.isInteger(n) || n < 1) return { error: 'Use a whole number of 1 or more.' };
  if (n > 10000) return { error: 'Use 10,000 or fewer.' };
  return { value: n };
}

export const Teams: React.FC = () => {
  const { user } = useAuth();
  const { groups, selectedGroupId, setSelectedGroupId, selectedGroup, refetchGroups, isLoading } = useGroup();
  const { subscription } = useEntitlements();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const churchId = user?.churchId;
  const { people } = useChurchPeople(churchId);
  const counts = useTeamHeadcounts(churchId);
  const [upcoming, setUpcoming] = useState<ServicePlan[]>([]);
  const [recent, setRecent] = useState<ServicePlan[]>([]);
  const [form, setForm] = useState<null | TeamForm>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null);
  const [toast, setToast] = useState<{ m: string; t: 'success' | 'error' } | null>(null);

  const maxGroups = getTierLimits(subscription?.tier ?? 'free').maxGroups;
  const atLimit = groups.length >= maxGroups;

  // Create → New worship team, and the team switcher's "+ New worship team".
  useEffect(() => {
    if (params.get('new') === '1') {
      setForm({ name: '', leader: user?.id ?? '', desc: '', cap: '' });
      params.delete('new');
      setParams(params, { replace: true });
    }
  }, [params, setParams, user?.id]);

  useEffect(() => {
    if (!churchId) return;
    plansApi.listUpcomingAll(churchId).then(setUpcoming).catch(() => setUpcoming([]));
  }, [churchId]);

  useEffect(() => {
    if (!churchId || !selectedGroupId) return;
    plansApi.getRecent(churchId, 100, selectedGroupId).then(setRecent).catch(() => setRecent([]));
  }, [churchId, selectedGroupId]);

  const nameOf = (id?: string) => people.find((p) => p.userId === id)?.name;
  // Approved people only; someone waiting in Requests isn't on the team yet.
  const peopleOn = (g: Group) => people.filter((p) => p.churchState === ChurchMembershipState.Active && p.groupIds.includes(g.id));

  // Positions people have actually served in on the active team.
  const positions = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const p of recent.filter((x) => x.groupId === selectedGroupId)) {
      for (const t of p.team) {
        if (!t.name || t.isPlaceholder) continue;
        map.set(t.role, (map.get(t.role) ?? new Set()).add(t.name));
      }
    }
    return Array.from(map.entries())
      .map(([role, names]) => ({ role, names: Array.from(names).sort() }))
      .sort((a, b) => b.names.length - a.names.length || a.role.localeCompare(b.role));
  }, [recent, selectedGroupId]);

  const saveTeam = async (e: FormEvent) => {
    e.preventDefault();
    if (!form || !churchId || !user) return;
    if (!form.name.trim()) {
      setForm({ ...form, err: 'Give the team a name.' });
      return;
    }
    const cap = parseCapacity(form.cap, form.currentCap);
    if (cap.error) {
      setForm({ ...form, capErr: cap.error });
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await groupsApi.update(form.id, {
          name: form.name.trim(),
          description: form.desc.trim() || undefined,
          leaderIds: form.leader ? [form.leader] : undefined,
          maxCapacity: cap.value,
          clearMaxCapacity: cap.clear || undefined,
          updatedBy: user.id,
        });
        setToast({ m: `"${form.name.trim()}" updated`, t: 'success' });
      } else {
        const request: GroupCreateRequest = {
          churchId,
          name: form.name.trim(),
          description: form.desc.trim() || undefined,
          leaderIds: [form.leader || user.id],
          maxCapacity: cap.value,
          createdBy: user.id,
        };
        const created = await groupsApi.create(churchId, request);
        setSelectedGroupId(created.id);
        setToast({ m: `${form.name.trim()} created. Plan its first service from Roster.`, t: 'success' });
      }
      setForm(null);
      await refetchGroups();
    } catch (err) {
      logger.error('Failed to save team', err);
      setToast({ m: form.id ? 'Failed to update team' : 'Failed to create team', t: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const setMode = async (g: Group, mode: string) => {
    try {
      await groupsApi.update(g.id, { setlistMode: mode });
      await refetchGroups();
      setToast({ m: `${g.name}: ${SETLIST_MODE_LABEL[mode as SetlistMode]} by default`, t: 'success' });
    } catch {
      setToast({ m: 'Could not update the setlist mode', t: 'error' });
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await groupsApi.delete(deleteTarget.id);
      setToast({ m: `"${deleteTarget.name}" deleted`, t: 'success' });
      await refetchGroups();
    } catch (err) {
      logger.error('Failed to delete team', err);
      setToast({ m: 'Failed to delete team', t: 'error' });
    }
  };

  const leaderOptions = people
    .filter((p) => p.churchState === 2)
    .map((p) => ({ value: p.userId, label: p.name, sub: p.role }));

  return (
    <div>
      {toast && <Toast message={toast.m} type={toast.t} onClose={() => setToast(null)} />}
      <PageHeader
        eyebrow="Each team has its own roster, setlists and availability"
        title="Worship teams"
        actions={
          <Button variant="dark" onClick={() => setForm({ name: '', leader: user?.id ?? '', desc: '', cap: '' })}>
            + New team
          </Button>
        }
      />

      {isLoading && !groups.length ? (
        <span className="wp-skeleton block h-56 rounded-[24px]" aria-busy="true" />
      ) : groups.length === 0 ? (
        <EmptyState title="No worship teams yet" body="Create a team to start planning services." action={<Button onClick={() => setForm({ name: '', leader: user?.id ?? '', desc: '', cap: '' })}>+ New team</Button>} />
      ) : (
        <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))' }}>
          {groups.map((g) => (
            <TeamCard
              key={g.id}
              group={g}
              active={g.id === selectedGroupId}
              memberNames={peopleOn(g).map((p) => p.name)}
              headcount={counts?.[g.id] ?? peopleOn(g).length}
              upcoming={upcoming.filter((p) => p.groupId === g.id && !isPastDate(p.date)).length}
              leader={nameOf(g.leaderIds?.[0] ?? g.leaderId) ?? 'Not set'}
              onPick={() => setSelectedGroupId(g.id)}
              onRoster={() => {
                setSelectedGroupId(g.id);
                navigate('/admin/roster');
              }}
              onMode={(m) => setMode(g, m)}
              onEdit={() =>
                setForm({
                  id: g.id,
                  name: g.name,
                  leader: g.leaderIds?.[0] ?? g.leaderId ?? '',
                  desc: g.description ?? '',
                  cap: g.maxCapacity ? String(g.maxCapacity) : '',
                  currentCap: g.maxCapacity ?? undefined,
                })
              }
              onDelete={() => setDeleteTarget(g)}
            />
          ))}
        </div>
      )}

      {selectedGroup && (
        <>
          <div className="flex justify-between items-baseline mt-8 mb-3">
            <h2 className="text-[18px] font-semibold">Positions on {selectedGroup.name}</h2>
            <span className="text-[13px] text-t4">From its recent services</span>
          </div>
          {positions.length === 0 ? (
            <p className="text-[13.5px] text-t4">No one has been scheduled on {selectedGroup.name} yet.</p>
          ) : (
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))' }}>
              {positions.map((p) => (
                <div key={p.role} className="rounded-[20px] border border-line bg-card p-[18px]">
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="text-[15px] font-semibold">{p.role}</span>
                    <span className="font-mono text-[20px] font-bold">{p.names.length}</span>
                  </div>
                  <div className="text-[13px] text-t4 mt-2 leading-normal">{p.names.map((n) => n.split(' ')[0]).join(', ')}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Edit worship team' : 'New worship team'}
        subtitle={form?.id ? undefined : 'It gets its own roster, setlists and availability. Songs stay shared across the church.'}
      >
        {form && (
          <form onSubmit={saveTeam} noValidate className="flex flex-col gap-4">
            {!form.id && atLimit && (
              <Banner tone="amb">
                You&apos;ve reached the {maxGroups === 1 ? 'one team' : `${maxGroups} teams`} your plan includes.{' '}
                <button type="button" className="underline font-semibold" onClick={() => navigate('/admin/subscription')}>
                  Upgrade
                </button>{' '}
                for more.
              </Banner>
            )}
            <Field label="Team name" htmlFor="team-name" error={form.err}>
              <Input id="team-name" data-autofocus value={form.name} invalid={!!form.err} onChange={(e) => setForm({ ...form, name: e.target.value, err: undefined })} placeholder="Youth Band" />
            </Field>
            <Field label="Team leader" htmlFor="team-leader">
              <Select id="team-leader" value={form.leader} onChange={(v) => setForm({ ...form, leader: v })} options={leaderOptions} placeholder="Choose a leader" />
            </Field>
            <Field label="What it serves" optional htmlFor="team-desc">
              <Input id="team-desc" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="Friday youth nights" />
            </Field>
            <Field
              label="Team size limit"
              optional
              htmlFor="team-cap"
              error={form.capErr}
              hint={
                form.currentCap
                  ? 'Raise or lower it, or clear it for no limit.'
                  : 'Leave blank for no limit. Once it’s full, no one else can join this team until you raise it.'
              }
            >
              <Input
                id="team-cap"
                inputMode="numeric"
                mono
                value={form.cap}
                invalid={!!form.capErr}
                placeholder="No limit"
                onChange={(e) => setForm({ ...form, cap: e.target.value.replace(/[^0-9]/g, ''), capErr: undefined })}
                className="!w-[140px]"
              />
            </Field>
            <ModalActions onCancel={() => setForm(null)} confirmLabel={form.id ? 'Save team' : 'Create team'} busy={saving} disabled={!form.id && atLimit} />
          </form>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        type="danger"
        title={deleteTarget ? `Delete ${deleteTarget.name}?` : 'Delete team?'}
        message="Its roster, services and setlists will no longer be on a team. You can move them to another team from Setlists."
        confirmText="Delete team"
      />
    </div>
  );
};

function TeamCard({
  group,
  active,
  memberNames,
  headcount,
  upcoming,
  leader,
  onPick,
  onRoster,
  onMode,
  onEdit,
  onDelete,
}: {
  group: Group;
  active: boolean;
  memberNames: string[];
  /** Approved people on the team, counted from membership rows. */
  headcount: number;
  upcoming: number;
  leader: string;
  onPick: () => void;
  onRoster: () => void;
  onMode: (mode: string) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <div className={cx('rounded-[24px] border-[1.5px] bg-card p-[22px] flex flex-col gap-3.5', active ? 'border-[rgba(208,48,69,.4)]' : 'border-line')}>
      <div className="flex justify-between items-start gap-2.5">
        <div className="min-w-0">
          <div className="text-[18px] font-semibold tracking-[-0.01em]">{group.name}</div>
          {group.description && <div className="text-[13px] text-t4 mt-[3px]">{group.description}</div>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {active && <StatusPill tone="acc">Viewing</StatusPill>}
          <button
            ref={ref}
            type="button"
            aria-label={`${group.name} options`}
            aria-haspopup="menu"
            aria-expanded={menu}
            onClick={() => setMenu(!menu)}
            className="w-8 h-8 rounded-full bg-fill hover:bg-fill2 text-t3 flex items-center justify-center"
          >
            <MoreHorizontal size={16} aria-hidden="true" />
          </button>
          <Menu open={menu} onClose={() => setMenu(false)} anchorRef={ref} label={`${group.name} options`} width={200} align="end">
            <MenuItem onSelect={() => { setMenu(false); onEdit(); }}>Edit team</MenuItem>
            <MenuDivider />
            <MenuItem danger onSelect={() => { setMenu(false); onDelete(); }}>Delete team</MenuItem>
          </Menu>
        </div>
      </div>
      {memberNames.length > 0 ? <AvatarStack names={memberNames} max={6} size={32} ring="card" /> : <p className="text-[13px] text-t5">No members yet</p>}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-fill rounded-[12px] px-3 py-2.5">
          <div className="font-mono text-[18px] font-bold">
            {headcount}
            {group.maxCapacity ? <span className="text-t4 text-[13px]">/{group.maxCapacity}</span> : null}
          </div>
          <div className={cx('text-[11.5px]', group.maxCapacity && headcount >= group.maxCapacity ? 'text-acc font-semibold' : 'text-t4')}>
            {group.maxCapacity && headcount >= group.maxCapacity ? 'Full' : 'People'}
          </div>
        </div>
        <div className="bg-fill rounded-[12px] px-3 py-2.5">
          <div className="font-mono text-[18px] font-bold">{upcoming}</div>
          <div className="text-[11.5px] text-t4">Upcoming</div>
        </div>
        <div className="bg-fill rounded-[12px] px-3 py-2.5 min-w-0">
          <div className="text-[13px] font-semibold truncate mt-[3px]">{leader}</div>
          <div className="text-[11.5px] text-t4 mt-0.5">Leader</div>
        </div>
      </div>
      <div>
        <label className="wp-label !mb-1.5" id={`mode-${group.id}`}>
          Setlists by default
        </label>
        <Select
          size="sm"
          aria-labelledby={`mode-${group.id}`}
          value={group.setlistMode || 'leader_builds'}
          onChange={onMode}
          options={[
            { value: 'leader_builds', label: SETLIST_MODE_LABEL.leader_builds, sub: 'The leader adds songs and publishes' },
            { value: 'admin_predefined', label: SETLIST_MODE_LABEL.admin_predefined, sub: 'An admin chooses the songs' },
          ]}
        />
      </div>
      <div className="flex gap-2">
        <Button variant="ghost" className="flex-1" onClick={onPick} disabled={active}>
          {active ? 'Viewing' : 'Switch to team'}
        </Button>
        <Button variant="dark" className="flex-1" onClick={onRoster}>
          View roster
        </Button>
      </div>
    </div>
  );
}
